import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Input, Image, Button } from "semantic-ui-react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFeatures } from "flagged";
import shortid from "shortid";

import axios from "axios";
import { baseUrl } from "@api/apiV6";
import { login } from "@reducers/authSlice";

import { Div } from "@components/Generics.react";
import AuthLayout from "@layouts/AuthLayout.react";
import { settings } from "../../config/settings/app.settings";

export default function CodeLogin(props) {
    const { team } = useParams();

    const [code, setCode] = useState("");
    const [fullName, setFullName] = useState("");
    const [email, setEmail] = useState("");

    const features = useFeatures();
    const dispatch = useDispatch();
    const [error, setError] = useState(null);
    const { t } = useTranslation("common");

    const tryRegister = async () => {
        const existingUser = window.localStorage.getItem("guestUsername");
        if (existingUser) {
            doLogin(existingUser);
        } else {
            // Split full name into first and last name
            const nameParts = fullName.trim().split(" ");
            const firstName = nameParts[0] || "Guest";
            const lastName = nameParts.slice(1).join(" ") || "User";

            // Use email as username if provided, otherwise generate one
            let username;
            let userEmail;

            if (email.trim()) {
                // Use email as username (remove spaces and convert to lowercase)
                username = email.trim().toLowerCase();
                userEmail = email.trim();
            } else {
                // Generate username and email if no email provided
                username = shortid.generate();
                userEmail = `${username}@guest.quodeck.com`;
            }

            let formData = new FormData();
            formData.append("username", username);
            formData.append("password", username);
            formData.append("email", userEmail);
            formData.append("first_name", firstName);
            formData.append("last_name", lastName);
            formData.append("team", team == null ? "Company" : team);
            formData.append("active", true);
            formData.append("createType", "putCodeUser");

            try {
                const signupResponse = await axios({
                    method: "post",
                    url: `${baseUrl}api/v3/auth/register`,
                    data: formData,
                    headers: { "Content-Type": "multipart/form-data" },
                });
                if (signupResponse.status === 201) {
                    window.localStorage.setItem("guestUsername", username);
                    if (features.tenanting.teamCourses) {
                        await axios({
                            method: "post",
                            url: `${baseUrl}api/v3/users/${signupResponse.data._id}/team_enrollment`,
                        });
                    }
                    doLogin(username);
                }
            } catch (error) {
                console.log(error);
                setError(error.response.data.message);
            }
        }
    };

    async function doLogin(uname) {
        let loginData = new FormData();
        loginData.append("username", uname);
        loginData.append("password", uname);
        const response = await axios({
            method: "post",
            url: `${baseUrl}api/v3/auth/sign_in`,
            data: loginData,
            headers: { "Content-Type": "multipart/form-data" },
        });
        await dispatch(
            login({
                user: response.data,
                token: response.headers["access-token"],
            })
        );
        window.localStorage.setItem("firstLogin", "done");
        if (team != null)
            window.localStorage.setItem("team", response.data.team);
        setTimeout(() => {
            window.location.href = `/c/${code}`;
        }, 1000);
    }

    const registrationBox = (
        <Div fluid superpad flex centered maxWd="480px">
            <Div fluid white fullHt superpad rounded>
                <Div big altText fluid center-align gutter>
                    {t("auth.enterCode")}
                </Div>
                <Div fluid gutter center-align>
                    <Input fluid>
                        <input
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            style={{ textAlign: "center" }}
                            placeholder={t("auth.enterCode")}
                        />
                    </Input>
                </Div>

                {/* Full Name Input */}
                <Div big altText fluid center-align gutter>
                    {t("auth.enterFullName")}
                </Div>
                <Div fluid gutter center-align>
                    <Input fluid>
                        <input
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            style={{ textAlign: "center" }}
                            placeholder={t("auth.enterFullName")}
                        />
                    </Input>
                </Div>

                {/* Email Input */}
                <Div big altText fluid center-align gutter>
                    {t("auth.enterEmail")}
                </Div>
                <Div fluid gutter center-align>
                    <Input fluid>
                        <input
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            style={{ textAlign: "center" }}
                            placeholder={t("auth.enterEmail")}
                            type="email"
                        />
                    </Input>
                </Div>

                {error && (
                    <Div padded fluid danger gutter>
                        {error}
                    </Div>
                )}

                <Div fluid gutter>
                    <Button
                        fluid
                        primary
                        content="SUBMIT"
                        onClick={tryRegister}
                        disabled={code.length < 9}
                    />
                </Div>
            </Div>
        </Div>
    );

    const { registrationActive, guestsActive } = settings.functionality;
    // ========================= Render Function =================================

    return (
        <AuthLayout
            title={`Register on ${t("appName")}`}
            team={team}
            teamLogin={true}
        >
            <Div fluid flex pageht>
                <Div maxWd={"480px"} centered>
                    <Div centered wd="60%" gutter>
                        <Image
                            fluid
                            src={
                                "/assets/images/configurable/full-logo-inverted.png"
                            }
                        />
                    </Div>
                    {registrationActive && guestsActive ? (
                        <Div fluid>{registrationBox}</Div>
                    ) : (
                        <Div basepad spaced white rounded big center-align>
                            Code based Login Not Available
                        </Div>
                    )}
                </Div>
            </Div>
        </AuthLayout>
    );
}
